import { Location } from '@angular/common';
import { inject, Injectable, Injector, signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, find, map as lmap } from 'lodash';
import { filter, take } from 'rxjs/operators';
import { TReducerState } from '../../../app.config';
import { PageHeaderService } from '../../../layout/page-header/page-header.service';
import { IDateRangePickerDate } from '../../../shared/components/date-range-picker/date-range-picker.interface';
import { FilterCardManager } from '../../../shared/components/filter-card/filter-card-manager';
import { IDropdownOption } from '../../../shared/components/mat-dropdown/mat-dropdown.component';
import { userActions } from '../../../state/user/user.actions';
import { TItemsComponentPageConfiguration } from '../../../state/user/user.interface';
import { DateUtilitiesService, TDatePickerRangeOption } from '../../../utilities/date-utilities.service';
import { IBoard } from '../../settings/boards/boards.interface';
import { TKpiCardKey } from '../items-kpi-cards/items-kpi-cards.interface';
import { ItemsScopePreferenceService } from '../items-scope-preference.service';
import { TStatusCategory, TUrlItemScope } from '../items.interface';
import {
  IItemsFilterCardFields,
  TCustomDateRangeConfigurationValue,
  TItemScope,
  TItemsFilterCardFieldName,
  TItemsFilterCardFields,
} from './items-filter-card.interface';
import { ItemsFilterCardStore } from './items-filter-card.store';

export type TItemsFilterCardManager = FilterCardManager<TItemsFilterCardFieldName>;

interface IItemsFilterCardClearedFilters {
  workflowStep: null;
}

/** Items-page behaviour that used to live inside `ItemsFilterCardComponent`. */
@Injectable()
export class ItemsFilterCardService {
  private readonly store = inject(ItemsFilterCardStore);
  private readonly globalStore: Store<TReducerState> = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly location = inject(Location);
  private readonly injector = inject(Injector);
  private readonly itemsScopePreference = inject(ItemsScopePreferenceService);

  private readonly user = this.globalStore.selectSignal((state: TReducerState) => state.user.user);
  private itemScopeValue: TUrlItemScope = null;

  public readonly itemScope: WritableSignal<TItemScope> = signal<TItemScope>(this.itemsScopePreference.itemScope());

  /**
   * The board comes from `ItemsResolver`, which runs in parallel with the filter card's own
   * resolver, so the page pushes it in once its route data is available. Must use
   * `patchInitialValue` (seeds the form before it's built), not `patchValue` (a live push that needs
   * a subscriber): this runs from the page's own `ngOnInit`, which fires before the filter card's.
   */
  public applyRouteContext(
    manager: TItemsFilterCardManager,
    initialBoard: IDropdownOption | undefined,
    itemScopeValue: TUrlItemScope,
  ): void {
    this.itemScopeValue = itemScopeValue;

    const itemScopeFromUrl: TItemScope = itemScopeValue === 'self' ? 'self' : 'family';

    this.itemScope.set(itemScopeValue ? itemScopeFromUrl : this.itemsScopePreference.itemScope());

    if (itemScopeValue && initialBoard) {
      manager.patchInitialValue({ board: [initialBoard] });
    }
  }

  /** Called when a KPI card is clicked; `null` means the card was deselected. */
  public applyKpiCardFilter(kpiCardKey: TKpiCardKey | null, manager: TItemsFilterCardManager): void {
    this.restoreItemScopeIfEscalatedCleared();
    const clearedFilters: IItemsFilterCardClearedFilters = { workflowStep: null };

    switch (kpiCardKey) {
      case null:
        manager.patchValue(clearedFilters, true);

        return;
      case 'total':
        manager.patchValue({ ...clearedFilters, itemType: null }, true);

        return;
      case 'escalated':
        this.itemScope.set('escalated');
        manager.patchValue(clearedFilters, true);

        return;
      default:
        this.applyStatusCategory(kpiCardKey, manager, clearedFilters);
    }
  }

  public persistAsDefault(value: Partial<IItemsFilterCardFields>): void {
    const componentConfigurations: TItemsComponentPageConfiguration[] =
      cloneDeep(this.user()!.pageConfiguration?.ItemsComponent) ?? [];

    if (
      !componentConfigurations.some(
        (componentConfiguration: TItemsComponentPageConfiguration) => componentConfiguration.name === 'filterCard',
      )
    ) {
      componentConfigurations.push({ name: 'filterCard', value: [] });
    }

    this.globalStore.dispatch(
      userActions.updateUserConfigurations({
        pageConfiguration: {
          ...(this.user()!.pageConfiguration ?? {}),
          ItemsComponent: componentConfigurations.map((componentConfiguration: TItemsComponentPageConfiguration) =>
            componentConfiguration.name === 'filterCard'
              ? { name: 'filterCard' as const, value: this.formatDataForSetAsDefault(value) }
              : componentConfiguration,
          ),
        },
      }),
    );
  }

  public updateUrlIfASingleBoardIsSelected(value: Partial<IItemsFilterCardFields>): void {
    const currentUrl: string = this.location.path();
    const scope: TUrlItemScope = currentUrl.split('/')[3] as TUrlItemScope;
    const boardKey: string | null = currentUrl.split('/')[2] as string;
    const isSelfSelected: boolean = value.itemScope === 'self';
    const selectedBoards: IDropdownOption[] | null | undefined = value.board;
    const selectedScope: TUrlItemScope = isSelfSelected ? 'self' : 'all';
    let newUrl: string = currentUrl;

    if (selectedBoards?.length !== 1) {
      const [, urlParams] = currentUrl.split('?');
      const urlParamsExt = urlParams ? `?${urlParams}` : '';
      this.location.go(`/items${urlParamsExt}`);
      PageHeaderService.setPageHeader({
        icon: 'format_list_bulleted',
        showFilterButton: true,
        subtitle: '',
        title: this.translate.instant('page.items.title'),
      });

      return;
    }

    if (scope === 'all' && isSelfSelected) {
      newUrl = currentUrl.replace(/all.*/, 'self');
    } else if (scope === 'self' && !isSelfSelected) {
      newUrl = currentUrl.replace(/self.*/, 'all');
    }

    this.store
      .select((state) => state.boards)
      .pipe(take(1))
      .subscribe((boards: IBoard[]) => {
        const selectedBoard: IBoard | undefined = find(boards, { id: selectedBoards[0].id });

        if (selectedBoard) {
          newUrl = boardKey
            ? newUrl.replace(new RegExp(`${boardKey}`), selectedBoard.key)
            : newUrl.replace(/items.*/, `items/${selectedBoard.key}/${selectedScope}`);

          PageHeaderService.setPageHeader({
            icon: 'grid_view',
            subtitle: selectedBoard.key,
            title: selectedBoard.name,
          });
        }
      });

    this.location.go(newUrl);
  }

  /**
   * Selecting any KPI card other than "Escalated" while it was selected must restore whatever scope
   * was active before — the `/self`|`/all` URL segment's implied scope, not unconditionally
   * `'family'`. A no-op when escalated wasn't selected (including the 'escalated' case itself, which
   * immediately overwrites this with `'escalated'` right after calling it).
   */
  private restoreItemScopeIfEscalatedCleared(): void {
    if (this.itemScope() === 'escalated') {
      this.itemScope.set(this.itemScopeValue === 'self' ? 'self' : 'family');
    }
  }

  private applyStatusCategory(
    statusCategory: TStatusCategory,
    manager: TItemsFilterCardManager,
    clearedFilters: IItemsFilterCardClearedFilters,
  ): void {
    toObservable(this.store.workflowSteps, { injector: this.injector })
      .pipe(
        filter((workflowSteps: IDropdownOption[]) => Boolean(workflowSteps.length)),
        take(1),
      )
      .subscribe((workflowSteps: IDropdownOption[]) => {
        manager.patchValue(
          {
            ...clearedFilters,
            workflowStep: workflowSteps.filter(
              (workflowStep: IDropdownOption) => workflowStep.statusCategory === statusCategory,
            ),
          },
          true,
        );
      });
  }

  /**
   * A named phrase (`'thisMonth'`, etc.) wins when the range matches one exactly — it stays portable
   * as "this month" rather than freezing today's actual dates. Otherwise the exact range is preserved
   * as UTC ISO strings (`TCustomDateRangeConfigurationValue`) instead of being silently dropped —
   * `convertRangeIntoDatePickerOption` returns `null` for anything that isn't one of the fixed phrases.
   */
  private encodeDateRangeForDefault(
    dateRange: IDateRangePickerDate | null | undefined,
  ): TCustomDateRangeConfigurationValue | TDatePickerRangeOption | null {
    const phrase = DateUtilitiesService.convertRangeIntoDatePickerOption(dateRange);

    if (phrase) {
      return phrase;
    }

    if (!dateRange?.startDate || !dateRange?.endDate) {
      return null;
    }

    return [
      { name: 'startDate', value: DateUtilitiesService.convertUserDateToUTCString(dateRange.startDate) },
      { name: 'endDate', value: DateUtilitiesService.convertUserDateToUTCString(dateRange.endDate) },
    ];
  }

  private formatDataForSetAsDefault(value: Partial<IItemsFilterCardFields>): TItemsFilterCardFields[] {
    const dateRange = this.encodeDateRangeForDefault(value.dateRange);
    const convertedFilters = {
      ...(value.assignee?.length ? { assignee: lmap(value.assignee, 'id') } : {}),
      ...(value.board?.length ? { board: lmap(value.board, 'id') } : {}),
      ...(dateRange ? { dateRange } : {}),
      ...(value.itemCategory?.length ? { itemCategory: lmap(value.itemCategory, 'id') } : {}),
      ...(value.itemType?.length ? { itemType: lmap(value.itemType, 'id') } : {}),
      ...(value.reporter?.length ? { reporter: lmap(value.reporter, 'id') } : {}),
      ...(value.workflowStep?.length ? { workflowStep: lmap(value.workflowStep, 'id') } : {}),
    };

    return Object.entries(convertedFilters).map(
      ([name, filterValue]) => ({ name, value: filterValue }) as TItemsFilterCardFields,
    );
  }
}
